import React, { useCallback, useMemo } from 'react'
import type { SelectProps as BaseSelectProps } from './Select';
import { Select } from './Select'
import type { SelectOption } from '@ur-solutions/react-components'
import type { CountryCode } from '@/utils/countries';
import { countries } from '@/utils/countries'
import { useCountryCodeTranslations } from '@/utils/hooks/useCountryCodeTranslations'

interface CountrySelectProps
  extends Omit<BaseSelectProps<CountryCode, string>, 'options'> {
  include?: CountryCode[]
  noTranslation?: boolean
  overrideName?: (code: CountryCode, name: string) => string
  overrideFlag?: (code: CountryCode, flag: string) => string
}

export const CountrySelect: React.FC<CountrySelectProps> = ({
  include,
  noTranslation = false,
  overrideName = (_, name) => name,
  overrideFlag = (_, flag) => flag,
  ...selectProps
}) => {
  // TODO fix exhaustive-deps?
  // eslint-disable-next-line
  const overrideNameCallback = useCallback(overrideName, [])
  // eslint-disable-next-line
  const overrideFlagCallback = useCallback(overrideFlag, [])

  const { getCountryCodeTranslations } = useCountryCodeTranslations()

  const translateName = useCallback(
    (code: CountryCode) => {
      return getCountryCodeTranslations(code)
    },
    [getCountryCodeTranslations]
  )

  const options = useMemo<SelectOption<CountryCode, string>[]>(
    () =>
      Object.entries(countries).reduce<SelectOption<CountryCode, string>[]>(
        (acc, [key, value]) => {
          if (
            typeof include !== 'undefined' &&
            !include.includes(key as CountryCode)
          )
            return acc

          let countryName = value.name
          if (!noTranslation) countryName = translateName(key as CountryCode)

          const name = overrideNameCallback(key as CountryCode, countryName)
          const flag = overrideFlagCallback(key as CountryCode, value.emoji)

          return [
            ...acc,
            {
              value: key as CountryCode,
              label: (
                <span>
                  <span className="flag">{flag}</span>&nbsp;&nbsp;
                  {name}
                </span>
              ),
              extra: name,
            },
          ]
        },
        []
      ),
    [
      include,
      noTranslation,
      translateName,
      overrideNameCallback,
      overrideFlagCallback,
    ]
  )

  const filterCountries = (
    option: SelectOption<CountryCode, string>,
    query: string
  ) => option.extra?.toLowerCase().includes(query.toLowerCase()) ?? true

  return (
    <Select options={options} searchable={filterCountries} {...selectProps} />
  )
}
